import type {
  CountrySpecificDocumentMapping,
  DocumentAndCountryConfiguration,
  IdDocKind,
} from '@onefootprint/request-types/dashboard';

/** The order the kinds appear in the node's checkbox group; the node subtitle lists them the same way. */
const ID_DOC_KINDS_IN_DISPLAY_ORDER: IdDocKind[] = [
  'drivers_license',
  'id_card',
  'passport',
  'passport_card',
  'visa',
  'residence_document',
  'permit',
  'voter_identification',
];

/**
 * Whether a kind is photographed on both sides, mirroring `DocumentKind::sides()` on the backend. A
 * total record, so adding a document kind fails to compile until its sides are declared here.
 */
const HAS_BACK_SIDE: Record<IdDocKind, boolean> = {
  drivers_license: true,
  id_card: true,
  passport: false,
  passport_card: true,
  visa: false,
  residence_document: true,
  permit: true,
  voter_identification: true,
};

type DocumentScope = 'any_country' | 'specific_countries';

type ScopedDocuments = { scope: DocumentScope; global?: IdDocKind[]; country?: CountrySpecificDocumentMapping };

/**
 * The backend expands a non-empty `global` to every issuing country before applying the per-country
 * lists, so that list alone decides whether the node is any-country or specific-countries.
 */
const getDocumentScope = ({ global }: Pick<DocumentAndCountryConfiguration, 'global'>): DocumentScope =>
  global.length > 0 ? 'any_country' : 'specific_countries';

/**
 * The form keeps `global` and blocked countries while the specific-countries radio hides them, so
 * switching back restores them; this is the only place they are dropped.
 */
const toDocumentTypesAndCountries = ({ scope, global, country }: ScopedDocuments): DocumentAndCountryConfiguration =>
  scope === 'any_country'
    ? { global: global ?? [], countrySpecific: country ?? {} }
    : {
        global: [],
        countrySpecific: Object.fromEntries(
          Object.entries(country ?? {}).filter(([, docs]) => (docs as IdDocKind[]).length > 0),
        ),
      };

const acceptsSomeDocument = ({ scope, global, country }: ScopedDocuments) =>
  scope === 'any_country'
    ? (global ?? []).length > 0
    : (Object.values(country ?? {}) as IdDocKind[][]).some(docs => docs.length > 0);

type InputDocumentSide = 'front' | 'back' | 'selfie';

/**
 * The sides input documents have to supply, mirroring the node's `expected_input_sides`: nobody is
 * there to choose a document, so they must cover every side any acceptable kind could ask for, and
 * the backend rejects an input set that doesn't match exactly.
 */
const expectedInputSides = ({
  global,
  countrySpecific,
  collectSelfie,
}: {
  global?: IdDocKind[];
  countrySpecific?: CountrySpecificDocumentMapping;
  collectSelfie?: boolean;
}): InputDocumentSide[] => {
  const kinds = [...(global ?? []), ...(Object.values(countrySpecific ?? {}) as IdDocKind[][]).flat()];
  return [
    'front',
    ...(kinds.some(kind => HAS_BACK_SIDE[kind]) ? (['back'] as const) : []),
    ...(collectSelfie ? (['selfie'] as const) : []),
  ];
};

export type { DocumentScope, InputDocumentSide };
export {
  acceptsSomeDocument,
  expectedInputSides,
  getDocumentScope,
  ID_DOC_KINDS_IN_DISPLAY_ORDER,
  toDocumentTypesAndCountries,
};
